import {
  ActionButton,
  AGTable,
  Button,
  ButtonMode,
  ButtonSize,
  CircleBar,
  colors,
  DataLoadError,
  Divider,
  Icon,
  ICONS,
  Loader,
  Tooltip,
} from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import { ReactElement, useMemo } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { ROUTES } from '../../Base.constants';
import useFetchUserPermissions from '../../hooks/permissions/useFetchUserPermissions';
import useFetchFundamental from '../../hooks/riskProducts/useFetchFundamental';
import { FundamentalAttr } from '../../models/FundamentalMetrics';
import { getRPCalculatorColumns, RPCalculatorAutoGroupColDef, RPCalculatorGridOptions } from './RPCalculator.constants';
import { convertToPercentage, getCqsColor } from './RPCalculator.logic';
import styles from './RPCalculator.module.scss';

const cx = classNames.bind(styles);

const CalculatorTable = AGTable<FundamentalAttr>;

const renderGroupRow = (params) => (
  <span className={cx('group-header', 'ag-cell-wrapper')}>
    <span>{params.value}</span>
  </span>
);

const RPCalculator = (): ReactElement => {
  const [searchParams] = useSearchParams();
  const { data: userPermissions } = useFetchUserPermissions();

  const companyId = useMemo(() => searchParams.get('companyId'), [searchParams]);
  const { data: fundamental, isLoading, isError, refetch } = useFetchFundamental(companyId);
  const { cqs, displayName, crl, cre, displayAttributes, currency } = fundamental || {};
  const columns = useMemo(() => getRPCalculatorColumns(currency), [currency]);
  const gridOptions = useMemo(
    () => ({
      ...RPCalculatorGridOptions,
      groupRowRenderer: renderGroupRow,
    }),
    []
  );

  const renderMetric = (label, value, originalValue) => (
    <div className={cx('metric')} data-testid={`${label}-metric`}>
      <span className={cx('header')}>{label}</span>
      <div className={cx('value')}>
        <Icon name={ICONS.CaretArrowUp} width={16} height={16} color={colors.tertiary400} />
        {value}
        <Icon name={ICONS.CaretArrowDown} width={16} height={16} color={colors.tertiary400} />
      </div>
      <div className={cx('footer')}>
        <span className={cx('title')}>Original</span>
        <span className={cx('original-value')}>{originalValue}</span>
      </div>
    </div>
  );

  const renderCircleMetric = (label, value, originalValue) => (
    <div className={cx('metric')} data-testid={`${label}-metric`}>
      <span className={cx('header')}>{label}</span>
      <div className={cx('circular-value')}>
        <CircleBar value={value} color={getCqsColor(cqs!)} />
      </div>
      <div className={cx('footer')}>
        <span className={cx('title')}>Original</span>
        <span className={cx('original-value')}>{originalValue}</span>
      </div>
    </div>
  );

  const hasAccess = userPermissions?.hasAccessToRP() && userPermissions?.hasAccessToNotProdReady();
  if (!hasAccess || !companyId) {
    return <Navigate to={ROUTES.notFound} replace />;
  }

  if (isLoading) {
    return <Loader className="loader-container" />;
  }

  if (isError) {
    return (
      <div className={cx('body')}>
        <DataLoadError onRetryClick={refetch} />
      </div>
    );
  }

  return (
    <div className={cx('body')}>
      <div className={cx('header')}>
        <div className={cx('side')}>
          <ActionButton
            icon={ICONS.CaretLeft}
            className={cx('action-item')}
            onClick={() => {}}
            tooltip="Back"
            dataTestId="back-button"
            small
            secondary
            dark
          />
          <Tooltip content={displayName}>
            <div className={cx('title')}>{displayName}</div>
          </Tooltip>
        </div>
        <ActionButton
          icon={ICONS.Download}
          onClick={() => {}}
          tooltip="Export"
          className={cx('action')}
          dataTestId="export-button"
          small
          secondary
          dark
          size={12}
        >
          Export
        </ActionButton>
      </div>
      <div className={cx('content')}>
        <div className={cx('metrics')}>
          {renderMetric('CRE', `${convertToPercentage(cre!, 2)}%`, `${convertToPercentage(cre!, 2)}%`)}
          <Divider vertical />
          {renderCircleMetric('CQS', cqs, cqs)}
          <Divider vertical />
          {renderMetric('CRL', crl, crl)}
        </div>
        <div className={cx('actions')}>
          <div className={cx('side')}>
            <Button mode={ButtonMode.PRIMARY} size={ButtonSize.SMALL}>
              Scenario 1
            </Button>
            <Button mode={ButtonMode.SECONDARY} size={ButtonSize.SMALL}>
              <Icon name={ICONS.Plus} width={12} height={12} color={colors.primary800} />
            </Button>
          </div>
          <Button className={cx('plain')} mode={ButtonMode.PLAIN} disabled>
            Reset
          </Button>
        </div>
        <CalculatorTable
          rowData={displayAttributes}
          columnDefs={columns}
          gridOptions={gridOptions}
          autoGroupColumnDef={RPCalculatorAutoGroupColDef}
          suppressMovableColumns
        />
      </div>
    </div>
  );
};

export default RPCalculator;
